import { useMemo, useState } from "react";
import { Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BreadcrumbSchema } from "@/components/SEO/BreadcrumbSchema";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, Leaf, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE_URL = "https://www.garden-review.co.uk";

export interface PlantGuideRow {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  featured_image_alt: string | null;
  category: string | null;
  categories: string[] | null;
  tags: string[] | null;
}

/** Filter tabs, mapped to the article categories used by the guides. */
export const plantTypeFilters: { label: string; categories: string[] }[] = [
  { label: "All guides", categories: [] },
  { label: "Perennials", categories: ["Perennials"] },
  { label: "Shrubs", categories: ["Shrubs"] },
  { label: "Trees", categories: ["Trees"] },
  { label: "Climbers", categories: ["Climbers"] },
  { label: "Bulbs", categories: ["Bulbs"] },
  { label: "Houseplants", categories: ["Indoor Plants", "House Plants"] },
  { label: "Problem solving", categories: ["Pest Control", "Disease Prevention", "Lawn Care"] },
];

export async function fetchPlantGuides(): Promise<PlantGuideRow[]> {
  const { data } = await supabase
    .from("articles")
    .select("id, title, slug, excerpt, featured_image, featured_image_alt, category, categories, tags")
    .eq("status", "published")
    .contains("tags", ["Plant Guide"])
    .order("title", { ascending: true })
    .limit(500);
  return (data as PlantGuideRow[] | null) || [];
}

export const plantGuidesQueryOptions = {
  queryKey: ["plant-guides"],
  queryFn: fetchPlantGuides,
};

/** "How to Grow Hydrangeas: Complete UK Care Guide" -> "Hydrangeas" */
const plantName = (title: string) =>
  title
    .replace(/^How to (Grow|Care for)\s+/i, "")
    .replace(/^(a|an|the)\s+/i, "")
    .split(":")[0]
    .trim();

const PlantsHub = ({ initialGuides }: { initialGuides?: PlantGuideRow[] | null }) => {
  const [activeFilter, setActiveFilter] = useState("All guides");
  const [search, setSearch] = useState("");

  const { data: guides = [], isLoading } = useQuery({
    ...plantGuidesQueryOptions,
    ...(initialGuides ? { initialData: initialGuides } : {}),
  });

  const filtered = useMemo(() => {
    const filter = plantTypeFilters.find((f) => f.label === activeFilter);
    const term = search.trim().toLowerCase();
    return guides.filter((g) => {
      if (filter && filter.categories.length > 0) {
        const cats = [g.category, ...(g.categories || [])].filter(Boolean) as string[];
        if (!cats.some((c) => filter.categories.includes(c))) return false;
      }
      if (term && !g.title.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [guides, activeFilter, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, PlantGuideRow[]>();
    for (const g of filtered) {
      const letter = plantName(g.title).charAt(0).toUpperCase() || "#";
      const key = /[A-Z]/.test(letter) ? letter : "#";
      map.set(key, [...(map.get(key) || []), g]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Plant Grow Guides",
    description:
      "A–Z of UK plant grow guides covering perennials, shrubs, climbers, bulbs and houseplants.",
    url: `${BASE_URL}/plants`,
    isPartOf: { "@type": "WebSite", name: "Garden Review", url: BASE_URL },
  };

  return (
    <div className="min-h-screen bg-background">
      <BreadcrumbSchema
        items={[
          { name: "Home", url: BASE_URL },
          { name: "Plants", url: `${BASE_URL}/plants` },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />
      <Header />

      <section className="relative overflow-hidden border-b py-2 md:py-2.5 bg-linear-to-br from-green-50 to-lime-50 dark:from-green-950/20 dark:to-lime-950/20">
        <div className="relative z-10 container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1.5 md:gap-4 py-1">
            <nav className="flex items-center gap-2 text-xs text-muted-foreground min-w-0" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground transition-colors whitespace-nowrap">Home</Link>
              <ChevronRight className="h-3 w-3 shrink-0" />
              <span className="text-foreground font-medium whitespace-nowrap">Plants</span>
            </nav>
            <div className="flex items-center gap-2.5 md:justify-end min-w-0">
              <h1 className="text-lg md:text-xl font-bold text-foreground whitespace-nowrap leading-tight">
                Plant Grow Guides
              </h1>
              <span className="hidden md:block text-foreground/30" aria-hidden="true">·</span>
              <p className="hidden md:block text-sm text-foreground/75 line-clamp-1 max-w-md">
                Expert UK planting, pruning and care advice, plant by plant
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <p className="text-muted-foreground max-w-3xl mb-6">
          Every guide is written for UK conditions, with planting depths, hardiness ratings,
          month-by-month care calendars and honest advice on the pests and diseases you are
          most likely to meet. Browse the A–Z below, or filter by plant type.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {plantTypeFilters.map((f) => (
            <button
              key={f.label}
              type="button"
              onClick={() => setActiveFilter(f.label)}
              aria-pressed={activeFilter === f.label}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                activeFilter === f.label
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative mb-8 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plants…"
            aria-label="Search plant guides"
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-lg" />
            ))}
          </div>
        )}

        {!isLoading && grouped.length === 0 && (
          <div className="text-center py-16">
            <Leaf className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">No guides match that search</h2>
            <p className="text-muted-foreground">Try a different plant name or filter.</p>
          </div>
        )}

        {grouped.map(([letter, items]) => (
          <section key={letter} className="mb-10" id={`letter-${letter}`}>
            <h2 className="text-2xl font-bold text-foreground mb-4 border-b pb-2">{letter}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((guide) => (
                <Link key={guide.id} to={`/articles/${guide.slug}`}>
                  <Card className="overflow-hidden hover:shadow-md transition-shadow h-full group">
                    {guide.featured_image && (
                      <img
                        src={guide.featured_image}
                        alt={guide.featured_image_alt || guide.title}
                        className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    )}
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {plantName(guide.title)}
                      </h3>
                      {guide.excerpt && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{guide.excerpt}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>

      <Footer />
    </div>
  );
};

export default PlantsHub;
